import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { LOCAL_TOPICS } from '@gogrov/shared'
import { PageChrome, ROUTES } from '@/components/gg/chrome'
import { usePlan, useAddRegion, useRemoveRegion, useOnboardingState, useTopics, CapReachedError } from '@/hooks/useConfig'
import { useClientPrefs, usePlaceDisclosure, placeKey } from '@/hooks/useMix'
import { usePlaces, useCoveredPlaces, useSwapPlace, useChangeHome, useNextPlan } from '@/hooks/useControls'
import { useHomePlace, usePinPlace } from '@/hooks/usePlaceCatalogue'
import { regionScope, type CataloguePlace } from '@/lib/placeCatalogue'
import { PlaceDial } from '@/components/places/PlaceDial'
import { useSeedBalance, SEED_COSTS } from '@/hooks/useSeeds'
import { useWatchState } from '@/hooks/useAlerts'
import { WATCH_TYPES } from '@/components/alerts/types'
import { SeedCost } from '@/components/gg/SeedChip'
import { tossSeeds, useSeedFx } from '@/lib/seedFx'
import type { Availability, Place } from '@/types/controls'
import { Sheet, useToast, Icon } from './ui'

/**
 * Places (Brian's after/settings-places.html v3, Oct 30: Add a place is the onboarding dial; v1.9 home change): where
 * your feed looks for stories. One row per place with its disclosure (records
 * available · partial · news only, and what it can supply per topic). The cap is
 * the plan's place count (5 · 10 · 30), a hard stop; at the cap Add a place
 * reads Full · Swap a place: pick the new place, then the one it replaces, for
 * 5 seeds. The home place counts, can't be removed or swapped out, and can be
 * changed (the same dial, for 5 seeds). A plan change never removes places.
 * The add sheet (v3): the places within a radius of home, larger first, the
 * border switch, then the county, state, National, other countries and the
 * World; everything else by search over the whole catalogue (PlaceDial).
 */

const COV: Record<Availability, [string, string]> = {
  records: ['Records available', 'ok'], partial: ['Partial records', ''], news: ['News only', ''],
}
type Mode = { kind: 'add' } | { kind: 'swap' } | { kind: 'home' } | { kind: 'out'; pick: CataloguePlace }

export function PlacesBoard() {
  const { data: places } = usePlaces()
  const { data: plan } = usePlan()
  const nextPlan = useNextPlan()
  const { data: onboarding } = useOnboardingState()
  const { data: prefs } = useClientPrefs()
  const { data: covered } = useCoveredPlaces()
  const { data: balance } = useSeedBalance()
  const addRegion = useAddRegion()
  const removeRegion = useRemoveRegion()
  const swap = useSwapPlace()
  const changeHome = useChangeHome()
  const pin = usePinPlace()
  const { data: mixRows } = useTopics()
  const releaseHold = useSeedFx((s) => s.release)
  const { data: watch } = useWatchState()
  const [toastNode, toast] = useToast()

  const list = places ?? []
  const limit = plan?.max_regions ?? 0
  const full = !!plan && list.length >= limit
  const price = SEED_COSTS.add_region
  const paid = !!onboarding?.onboarded
  const home = list.find((p) => p.home) ?? list[0]
  const { data: homePlace } = useHomePlace(home, prefs?.state)
  const mix = (mixRows ?? []).map((t) => ({ topic: t.topic, share: t.share ?? 0 }))
  // which catalogue place a new region is (its region line, its point); a failure here never undoes the add
  const pinTo = (regionId: string | null | undefined, c: CataloguePlace) => { if (regionId) pin.mutate({ regionId, placeId: c.id }) }

  // what each place can supply, per local topic (place_capabilities)
  const keyOf = (p: Place) => placeKey(p.name, prefs?.state)
  const keys = useMemo(() => [...new Set([...list.map(keyOf), ...(covered ?? []).map((c) => c.place_key)])],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list.map((p) => p.name).join('|'), covered, prefs?.state])
  const { data: disclosure } = usePlaceDisclosure(keys)
  const supply = (key: string) => (disclosure ?? []).filter((d) => d.place_key === key)
  const level = (key: string): Availability => {
    const s = supply(key)
    return s.some((d) => d.availability === 'records') ? 'records' : s.some((d) => d.availability === 'partial') ? 'partial' : 'news'
  }
  // Watch (Brian v1.9): breaking alerts run for that place. Local watches
  // (Safety, Weather) run where the account lives; a watch whose subject names
  // a place runs there.
  const localWatches = (watch?.paused ? [] : watch?.watches ?? []).filter((w) => WATCH_TYPES[w.type]?.scope === 'local')
  const watched = (name: string, isHome = false) => {
    const n = name.toLowerCase()
    return localWatches.some((w) => w.subject.toLowerCase().includes(n)) || (isHome && localWatches.length > 0)
  }
  const watchChip = (name: string, isHome = false) => watched(name, isHome) ? <span className="chip watch">Watch</span> : null
  const regionOf = (p: Place) => p.region ?? (covered ?? []).find((c) => c.place_key === keyOf(p))?.region ?? cap1(p.scope)

  const [openRow, setOpenRow] = useState<string | null>(null)
  const [mode, setMode] = useState<Mode | null>(null)
  const [sheetOn, setSheetOn] = useState(false)
  const open = (m: Mode) => { setMode(m); setSheetOn(true) }
  const close = () => { setSheetOn(false); window.setTimeout(() => setMode(null), 260) }

  const seeds = balance?.total_available ?? 0
  function afford(cost: number, what: string) {
    if (!paid || seeds >= cost) return true
    toast(<><b className="al">Not enough seeds.</b> {what} is {cost}, you have {seeds}.</>, 3400)
    return false
  }

  async function pick(c: CataloguePlace) {
    if (!mode) return
    const scope = regionScope(c)
    if (mode.kind === 'home') {
      if (!afford(price, 'A change')) return
      const cur = home?.name ?? ''
      close(); tossSeeds(price)
      try { pinTo(await changeHome.mutateAsync({ name: c.name, scope }), c); toast(<>{c.name} is your home place now; {cur} is out. Tomorrow’s brief follows.</>, 3200) }
      catch (e) { releaseHold(); toast((e as Error).message, 3400) }
      return
    }
    if (mode.kind === 'swap' || full) {
      if (!afford(price, 'A swap')) return
      setMode({ kind: 'out', pick: c }); return
    }
    if (!afford(price, 'A place')) return
    close(); if (paid) tossSeeds(price)
    addRegion.mutate({ name: c.name, scope }, {
      onSuccess: (id) => { pinTo(id, c); toast(<>{c.name} added. Tomorrow’s brief looks there too.</>) },
      onError: (e) => { releaseHold(); toast(e instanceof CapReachedError ? `You are at your plan’s ${limit} places.` : (e as Error).message, 3400) },
    })
  }
  async function swapOut(c: CataloguePlace, out: Place) {
    close(); tossSeeds(price)
    try { pinTo(await swap.mutateAsync({ outId: out.id, name: c.name, scope: regionScope(c) }), c); toast(<>{c.name} in, {out.name} out. Tomorrow’s brief follows.</>, 3200) }
    catch (e) { releaseHold(); toast((e as Error).message, 3400) }
  }

  // choosing a new home: only the current home is greyed; an added place can move up to home
  const held = list.filter((p) => mode?.kind !== 'home' || p.home)
  const have = (c: CataloguePlace) => held.some((p) => p.place_id === c.id
    || (p.place_id == null && p.name.toLowerCase() === c.name.toLowerCase() && p.scope === regionScope(c)))

  const why = `${plan?.name ?? 'Your plan'} covers up to ${limit} places, your home place included.`
  const over = list.length > limit

  return (
    <div className="fx-page fx-places">
      <PageChrome title="Places" navKey="places" back={{ to: ROUTES.feedControls, label: 'Feed controls' }} />
      <p className="fx-lead">Where your feed looks for stories. Every story comes from one of these places.</p>
      <div className="fx-count"><span>Your places</span><b>{list.length} of {limit} places · {plan?.name ?? ''}</b></div>

      <div className="fx-placelist">
        {list.map((p) => {
          const k = keyOf(p), lv = level(k), [lbl, cls] = COV[lv], isOpen = openRow === p.id
          const s = supply(k)
          return (
            <div key={p.id} className={`fx-place${p.home ? ' home' : ''}`}>
              <div className="main">
                <span className="pin">{Icon.pin}</span>
                <div className="t">
                  <div className="n">{p.name}{p.home && <small>Home</small>}</div>
                  <div className="r">{regionOf(p)}</div>
                  <div className="cov">
                    <span className={`chip ${cls}`}>{lbl}</span>{watchChip(p.name, !!p.home)}
                    <button type="button" className="disc" aria-expanded={isOpen} onClick={() => setOpenRow(isOpen ? null : p.id)}>
                      What it can supply{Icon.chev}
                    </button>
                  </div>
                </div>
                {p.home
                  ? <button type="button" className="x chg" aria-label="Change your home place" onClick={() => open({ kind: 'home' })}>Change</button>
                  : <button type="button" className="x" aria-label={`Remove ${p.name}`} disabled={removeRegion.isPending}
                      onClick={() => removeRegion.mutate(p.id, { onSuccess: () => toast(`${p.name} removed.`), onError: (e) => toast((e as Error).message) })}>×</button>}
              </div>
              {isOpen && <Supply rows={s} />}
            </div>
          )
        })}
        {list.length === 0 && <p className="fx-none">No places yet. Add your town to start seeing stories.</p>}
      </div>

      <button type="button" className={`fx-add${full ? ' full' : ''}`} disabled={full && list.length < 2}
        onClick={() => open(full ? { kind: 'swap' } : { kind: 'add' })}>
        {full ? 'Full · Swap a place' : 'Add a place'}{paid && <SeedCost n={price} />}
      </button>

      <div className="fx-note">
        <b>What a place costs.</b> {full ? `A swap is ${price} seeds.` : `Adding one is ${price} seeds, once.`} {why} You can always change them: when you’re full, swap one place for another. Your home place comes from setup; Change swaps it for another, for {price} seeds.
        {over && <> <b>You’re over the cap</b> after a plan change: nothing was removed, and you can still swap.</>} Records, partial, or news only says how deep the sources go there; Watch means breaking alerts run for that place.
      </div>
      <p className="fx-foot"><Link to={ROUTES.topics}>Topics</Link> decide what your feed looks for in these places.</p>

      <Sheet open={sheetOn} onClose={close} label={mode?.kind === 'home' ? 'Change your home place' : mode?.kind === 'add' ? 'Add a place' : 'Swap a place'}>
        {mode && mode.kind !== 'out' && (
          <div className="fx-in">
            <h3>{mode.kind === 'home' ? 'Change your home place' : mode.kind === 'swap' ? 'Swap a place' : 'Add a place'}</h3>
            <p className="sub">
              {mode.kind === 'home' ? `The place your brief is tuned to first, in place of ${home?.name ?? 'your home'}, which stops feeding it tomorrow. A change uses ${price} seeds.`
                : mode.kind === 'swap' ? `${plan?.name} covers up to ${limit} places. Pick the new place, then the one it replaces. A swap uses ${price} seeds.`
                : `Any city, county, state or country. Public records run in the US; everywhere else it’s the news, in the place’s language. A place uses ${price} seeds, once.`}
            </p>
            <PlaceDial home={homePlace} have={have} mix={mix} onPick={(c) => void pick(c)} />
            {mode.kind === 'swap' && nextPlan && (
              <div className="up"><span>{nextPlan.name} covers {nextPlan.max_regions} places.</span><Link to={`${ROUTES.settings}?tab=plan`} onClick={close}>See {nextPlan.name}</Link></div>
            )}
          </div>
        )}
        {mode?.kind === 'out' && (
          <div className="fx-in">
            <h3>{mode.pick.name} instead of…</h3>
            <p className="sub">The place you let go of stops feeding your brief tomorrow. You can swap it back any time, for {price} seeds.</p>
            <div className="swapfor">Your places</div>
            <div className="opts">
              {list.filter((p) => !p.home).map((p) => {
                const [lbl, cls] = COV[level(keyOf(p))]
                return (
                  <button key={p.id} type="button" className="opt" disabled={swap.isPending} onClick={() => void swapOut(mode.pick, p)}>
                    <span className="t"><span className="n">{p.name}</span><span className="r">{regionOf(p)}</span>
                      <span className="cov"><span className={`chip ${cls}`}>{lbl}</span></span></span>
                    <span className="cost">Replace</span>
                  </button>
                )
              })}
            </div>
            <button type="button" className="back2" onClick={() => setMode({ kind: 'swap' })}>‹ Pick a different place</button>
          </div>
        )}
      </Sheet>
      {toastNode}
    </div>
  )
}

const cap1 = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

/** What a place can supply, per local topic, in three plain states. */
function Supply({ rows }: { rows: { topic: string; availability: Availability }[] }) {
  const by = (a: Availability) => LOCAL_TOPICS.filter((t) => (rows.find((r) => r.topic === t.id)?.availability ?? 'news') === a).map((t) => t.label)
  const groups: [Availability, string, string][] = [
    ['records', 'Records', 'Published records (permits, agendas, filings), so a story can come straight from the source.'],
    ['partial', 'Partial records', 'One office publishes, another does not.'],
    ['news', 'News only', 'Stories come from what outlets report.'],
  ]
  return (
    <div className="supply">
      {groups.map(([a, title, line]) => {
        const names = by(a)
        if (!names.length) return null
        return (
          <div key={a} className="g">
            <b className={a === 'records' ? 'ok' : ''}>{title}</b><small>{line}</small>
            <span>{names.join(', ')}</span>
          </div>
        )
      })}
    </div>
  )
}
